import { RoadResponseDTO } from "../api/response.types";
import {
  RoadEventModel,
  RoadFreightModel,
  RoadFromModel,
  RoadLegModel,
  RoadLocationModel,
  RoadRouteModel,
  RoadToModel,
  RoadTransformedEventModel,
} from "./types";
import { roadErrorMessageMap, RoadMessagesModel, RoadStatusesModel } from "entities/shared/roadCard.types";
import {
  CoordinateModel,
  DateTimeModel,
  SearchReferenceDTO,
  ShipmentVariantModel,
} from "entities/shared/shipmentCard.types";
import { RouteDataType, TransportTypes } from "shared/api/lib/map.types";
import { MapRoute, MapRouteModel } from "entities/shared/mapRouteModel";
import { SubscriptionDataModel, SubscriptionModel } from "entities/shared/subscriptionModel";
import { BaseModel } from "shared/api/lib/baseModel";
import { Nullable, ValueOf } from "shared/lib/typeCheck.lib";
import { getUtcTimestamp } from "shared/utils/dateTime.utils";
import { isAppIntegration } from "shared/utils/url.utils";

import { SearchApiParser } from "../api/parser";

export const isRoadSearchShipmentModel = (model: Nullable<{ responseType: string }>): model is RoadSearchCardModel =>
  model?.responseType === ShipmentVariantModel.RoadSearch;

export class RoadSearchCardModel extends BaseModel<SearchApiParser, RoadResponseDTO> {
  public readonly responseType: ShipmentVariantModel;
  public readonly requestLimitReachedAppIntegration: boolean = false;
  public readonly requestLimitReachedWebApp: boolean = false;
  private readonly _subscription: SubscriptionModel | null = null;
  private _pol: string | null = null;
  private _pod: string | null = null;
  private _route: MapRoute | null = null;

  constructor(DTO: RoadResponseDTO) {
    super({ apiParser: new SearchApiParser(DTO), DTO });

    this._subscription = new SubscriptionModel(this.apiParser.metadata);

    this.setLocations();

    this._route = new MapRoute(this.adaptRouteData());

    this.responseType = ShipmentVariantModel.RoadSearch;
    this.requestLimitReachedAppIntegration = this.requestLimitReached && isAppIntegration;
    this.requestLimitReachedWebApp = this.requestLimitReached && !isAppIntegration;
  }

  get isSuccess(): boolean {
    return this.apiParser.isSuccess;
  }

  get requestLimitReached(): boolean {
    return this.apiParser.requestLimitReached;
  }

  get subscription(): SubscriptionDataModel | null {
    return this._subscription?.data ?? null;
  }

  get reference(): SearchReferenceDTO {
    return this.apiParser.number;
  }

  get type(): string | null {
    return this.apiParser.type;
  }

  get scac(): string | null {
    return this.apiParser.scac;
  }

  get status(): ValueOf<typeof RoadStatusesModel> {
    return this.apiParser.status;
  }

  get pieces(): string {
    return this.apiParser.pieces;
  }

  get carrierName(): string {
    return this.apiParser.carrierName;
  }

  get fromLocation(): RoadFromModel {
    return this.apiParser.fromLocation;
  }

  get toLocation(): RoadToModel {
    return this.apiParser.toLocation;
  }

  get freight(): RoadFreightModel {
    return this.apiParser.freight;
  }

  get locations(): RoadLocationModel[] {
    return this.apiParser.locations;
  }

  get polCoordinate(): CoordinateModel | null {
    return this.apiParser.polCoordinate;
  }

  get podCoordinate(): CoordinateModel | null {
    return this.apiParser.podCoordinate;
  }

  get legs(): RoadLegModel[] {
    return this.apiParser.legs;
  }

  get routes(): RoadRouteModel[] {
    return this.legs.map((leg): RoadRouteModel => {
      const fromLocation = this.locations.find((location) => location.id === leg.from?.location);
      const toLocation = this.locations.find((location) => location.id === leg.to?.location);

      return {
        from: {
          ...leg.from,
          location: this.getLocationName(fromLocation) || "",
        },
        to: {
          ...leg.to,
          location: this.getLocationName(toLocation) || "",
        },
        atd: this.atd,
        etd: this.etd,
        ata: this.ata,
        eta: this.eta,
      };
    });
  }

  get etd(): Nullable<DateTimeModel> {
    return this.apiParser.etd;
  }

  get atd(): Nullable<DateTimeModel> {
    return this.apiParser.atd;
  }

  get eta(): Nullable<DateTimeModel> {
    return this.apiParser.eta;
  }

  get ata(): Nullable<DateTimeModel> {
    return this.apiParser.ata;
  }

  get progress(): number {
    return this.calcProgress({
      startDate: this.atd ?? this.etd,
      endDate: this.ata ?? this.eta,
      status: this.status,
    });
  }

  private setLocations() {
    this._pol = this.getLocationName(this.locations[0]);
    this._pod = this.getLocationName(this.locations[this.locations.length - 1]);
  }

  get fromDirection(): string | null {
    return this._pol;
  }

  get toDirection(): string | null {
    return this._pod;
  }

  public getLocationName(location: Nullable<RoadLocationModel>): string | null {
    if (!location) return null;

    // Priority: name -> city (with country_code) -> address -> locode
    if (location.name) return location.name;
    if (location.city) {
      return location.country_code ? `${location.country_code} ${location.city}` : location.city;
    }
    if (location.address) return location.address;
    if (location.locode) return location.locode;

    return null;
  }

  private calcProgress({
    startDate,
    endDate,
    status,
  }: {
    startDate: Nullable<DateTimeModel>;
    endDate: Nullable<DateTimeModel>;
    status: ValueOf<typeof RoadStatusesModel>;
  }): number {
    const notStartedStatusesSet = new Set<ValueOf<typeof RoadStatusesModel>>([
      RoadStatusesModel.BOOKED,
      RoadStatusesModel.RECEIVED,
    ]);
    const completedStatusesSet = new Set<ValueOf<typeof RoadStatusesModel>>([
      RoadStatusesModel.NOTIFIED,
      RoadStatusesModel.DELIVERED,
      RoadStatusesModel.ARRIVED,
    ]);
    if (notStartedStatusesSet.has(status)) return 0;
    if (completedStatusesSet.has(status)) return 100;
    if (status === RoadStatusesModel.CANCELLED) return 0;

    if (!startDate || !endDate || startDate === endDate) return 0;

    const start: number = getUtcTimestamp(startDate);
    const end: number = getUtcTimestamp(endDate);
    const today: number = getUtcTimestamp(new Date());

    const totalTime = Math.abs(end - start);
    const elapsedTime = Math.abs(today - start);

    let percentageElapsed: number;

    if (elapsedTime <= 0) {
      percentageElapsed = 0;
    } else if (elapsedTime >= totalTime) {
      percentageElapsed = 100;
    } else {
      percentageElapsed = Math.round((elapsedTime / totalTime) * 100);
    }

    return percentageElapsed;
  }

  public searchErrorText(defaultText: string): string | null {
    const searchMessage = this.apiParser.responseStatus;
    if (searchMessage === RoadMessagesModel.OK) return null;
    return roadErrorMessageMap[searchMessage] ?? defaultText;
  }

  get events(): RoadEventModel[] {
    return this.apiParser.events ?? [];
  }

  public getTransformedEvents(targetEvents: Nullable<RoadEventModel[]>): RoadTransformedEventModel[] {
    if (!targetEvents) return [];

    const events: RoadEventModel[] = [];
    const transformedEvents: RoadTransformedEventModel[] = [{ location: null, events }];
    let prevLocation: RoadLocationModel | null = null;

    targetEvents.forEach((event, i) => {
      const location = this.locations.find((location) => location.id === event.location);
      const locationName = this.getLocationName(location);

      if (i === 0) {
        transformedEvents[0].location = locationName;
        transformedEvents[0].events.push(event);
      } else {
        if (
          prevLocation !== null &&
          prevLocation?.name === location?.name &&
          prevLocation?.city === location?.city &&
          prevLocation?.country_code === location?.country_code
        ) {
          transformedEvents[transformedEvents.length - 1].events.push(event);
        } else {
          transformedEvents.push({ location: locationName, events: [event] });
        }
      }

      prevLocation = location ?? null;
    });

    return transformedEvents;
  }

  private adaptRouteData(): MapRouteModel {
    const legs = this.legs ?? [];
    const lastLeg = legs[legs.length - 1];

    const findLocation = (locationId?: string) => this.locations.find((loc) => loc.id === locationId);

    return {
      route: legs
        .filter((leg) => leg.path?.length)
        .map((leg) => ({
          path: leg.path ?? [],
          type: RouteDataType.LAND,
          transportType: TransportTypes[leg.transport?.type] ?? TransportTypes.TRUCK,
        })),
      locations: [
        ...legs.map((leg, index) => {
          const from = findLocation(leg.from?.location);
          return {
            id: index,
            name: from?.name ?? "",
            country: from?.country ?? "",
            countryCode: from?.country_code ?? "",
            locode: from?.locode,
            lat: from?.lat,
            lng: from?.lng,
          };
        }),
        ...(() => {
          const to = findLocation(lastLeg?.to?.location);
          if (!to) return [];
          return [
            {
              name: to.name ?? "",
              country: to.country ?? "",
              countryCode: to.country_code ?? "",
              locode: to.locode,
              lat: to.lat,
              lng: to.lng,
            },
          ];
        })(),
      ],
    };
  }

  get route(): MapRoute | null {
    return this._route;
  }
}
